import { deg2Rad } from './math'
const { PI } = Math

const startDraw = ctx => draw => {
  ctx.save()
  draw(ctx)
  ctx.restore()
}

export const drawLine = ctx => ({
  x1, y1,
  x2, y2,
  lineWidth = 2,
  dashed = false,
  color = 'grey'
}) => {
  startDraw(ctx)(ctx => {
    ctx.lineCap = 'round'
    ctx.lineWidth = lineWidth
    ctx.strokeStyle = color

    if (dashed) {
      ctx.setLineDash([6, 4])
    }

    ctx.beginPath()
    ctx.moveTo(x1, y1)
    ctx.lineTo(x2, y2)
    ctx.stroke()
  })
}

export const drawPoint = ctx => ({
  x, y,
  style = 'cross', // cross, circle
  fill = false, // only works for circle style
  color = 'green'
}) => {
  const line = drawLine(ctx)
  const circle = drawCircle(ctx)
  const r = 5

  startDraw(ctx)(ctx => {
    if (style === 'cross') {
      line({ x1: x - r, y1: y - r, x2: x + r, y2: y + r, color })
      line({ x1: x + r, y1: y - r, x2: x - r, y2: y + r, color })
    } else if (style === 'circle') {
      circle({ x, y, r, color, fill })
    }
  })
}

export const drawCircle = ctx => ({
  x, y, r,
  fill = false,
  lineWidth = 2,
  dashed = false,
  color = 'black'
}) => {
  startDraw(ctx)(ctx => {
    ctx.beginPath()
    
    ctx.lineWidth = lineWidth
    if (dashed) {
      ctx.setLineDash([6, 4])
    }

    ctx.arc(x, y, r, 0, Math.PI * 2)
    if (fill) {
      ctx.fillStyle = color
      ctx.fill()
    } else {
      ctx.strokeStyle = color
      ctx.stroke()
    }
  })
}

export const drawCartesan = ctx => ({
  x, y, r, color = 'gray'
}) => {
  const line = drawLine(ctx)
  const circle = drawCircle(ctx)

  startDraw(ctx)(ctx => {
    ctx.strokeStyle = color
    circle({ x, y, r, color: 'gray'})
    line({ x1: x - r, y1: y, x2: x + r, y2: y })
    line({ x1: x, y1: y - r, x2: x, y2: y + r })
  })
}

export const draw45Arc = ctx => ({
  x, y, r,
  color = 'gray',
  dashed
}) => {
  const line = drawLine(ctx)

  startDraw(ctx)(() => {
    let delta, cosDelta, sinDelta
    let epsi, sinEp, tanR
    let phi, xf, yf, xi = -r, yi = 0
    let i = -45

    delta = deg2Rad(i)
    cosDelta = Math.cos(delta)
    sinDelta = Math.sin(delta)

    for (let j = 5; j < 185; j += 5) {
      epsi = deg2Rad(j)
      sinEp = Math.sin(epsi)
      tanR = Math.tan(Math.acos(cosDelta * sinEp) / 2)

      if (j === 90) {
        phi = PI / 2
      } else {
        phi = Math.atan(sinEp * sinDelta / Math.cos(epsi))
      }
        
      if (phi < 0) {
        phi += PI
      }

      xf = r * Math.cos(phi) * tanR
      yf = -r * Math.sin(phi) * tanR
  
      line({ x1: x + yi, y1: y - xi, x2: x + yf, y2: y - xf, color, dashed })
      xi = xf
      yi = yf
    }   
  })
}

export const drawWulff = ctx => ({
  x, y, r,
  primaryColor = 'gray',
  secondaryColor = 'silver'
}) => {
  const line = drawLine(ctx)
  const circle = drawCircle(ctx)

  startDraw(ctx)(ctx => {
    let delta, cosDelta, sinDelta, tanDelta
    let epsi, sinEp, tanR
    let phi, xf, yf, xi, yi
    let color

    // draw verticle arcs
    for (let i = -85; i < 5; i += 5) {
      delta = deg2Rad(i)
      cosDelta = Math.cos(delta)
      sinDelta = Math.sin(delta)
      color = i % 10 === 0 ? primaryColor : secondaryColor

      for (let j = 10; j < 175; j += 5) {
        epsi = deg2Rad(j)
        sinEp = Math.sin(epsi)
        tanR = Math.tan(Math.acos(cosDelta * sinEp) / 2)

        if (j === 90) {
          phi = PI / 2
        } else {
          phi = Math.atan(sinEp * sinDelta / Math.cos(epsi))
        }
        if (phi < 0) {
          phi += PI
        }

        xf = r * Math.cos(phi) * tanR
        yf = -r * Math.sin(phi) * tanR

        if (j === 10) {
          xi = xf
          yi = yf
        }

        line({ x1: x - yi, y1: y + xi, x2: x - yf, y2: y + xf, color }) // top right part
        line({ x1: x + yi, y1: y - xi, x2: x + yf, y2: y - xf, color }) // left bottom part

        xi = xf
        yi = yf
      }
    }

    // draw horizontal arcs
    for (let i = 5; i < 175; i += 5) {
      delta = deg2Rad(i)
      tanDelta = Math.tan(delta)
      sinDelta = Math.sin(delta)
      color = i % 10 === 0 ? primaryColor : secondaryColor

      for (let j = 0; j < 190; j += 10) {
        epsi = deg2Rad(j)
        sinEp = Math.sin(epsi)
        tanR = Math.tan(Math.acos(sinDelta * sinEp) / 2)

        if (j === 90) {
          phi = PI / 2
        } else {
          phi = Math.atan(1 / (tanDelta * Math.cos(epsi)))
        }
        if (phi < 0) {
          phi += PI
        }

        yf = r * Math.cos(phi) * tanR
        xf = -r * Math.sin(phi) * tanR

        if (j === 0) {
          xi = xf
          yi = yf
        }

        line({ x1: x + yi, y1: y - xi, x2: x + yf, y2: y - xf, color })
        line({ x1: x - yi, y1: y + xi, x2: x - yf, y2: y + xf, color })

        xi = xf
        yi = yf
      }
    }

    circle({ x, y, r, color: primaryColor })
    circle({ x, y, r: r / 40, color: primaryColor })
  })
}

export const drawPolar = ctx => ({
  x, y, r,
  primaryColor = 'gray',
  secondaryColor = 'silver'
}) => {
  const line = drawLine(ctx)
  const circle = drawCircle(ctx)
  const tan5Rad = Math.tan(deg2Rad(5))

  startDraw(ctx)(ctx => {
    let ph, rh, cosPh, sinPh
    let xi, yi, xf, yf
    let color

    circle({ x, y, r, color: primaryColor })

    for (let i = 0; i < 190; i += 5) {
      ph = deg2Rad(i)
      cosPh = r * Math.cos(ph)
      sinPh = r * Math.sin(ph)
      xi = cosPh
      yi = sinPh
      xf = (cosPh * tan5Rad)
      yf = (sinPh * tan5Rad)
      color = i % 10 === 0 ? primaryColor : secondaryColor

      line({ x1: x + xi, y1: y - yi, x2: x + xf, y2: y - yf, color })
      line({ x1: x - xi, y1: y + yi, x2: x - xf, y2: y + yf, color })
    }

    for (let i = 5; i < 90; i += 5) {
      rh = deg2Rad(i * .5)
      xi = r * Math.tan(rh)
      color = i % 10 === 0 ? primaryColor : secondaryColor

      circle({ x, y, r: xi, color })
    }
  })
}



// WEBPACK FOOTER //
// ./src/utils/draw.js